import { z } from 'zod';

import { NumericIdSchema } from './mice_refs';

// Perguntas do evento: o organizador as faz e cada convidado responde no convite (RSVP). Só
// importa a folha `mice_refs`: ./mice e ./mice_invites importam daqui.
//
// Nada é apagado: a pergunta e a opção saem arquivadas (`archivedAt`, `archived`) e as respostas
// já dadas continuam no resumo do organizador. Pergunta nova depois de alguém responder não
// trava ninguém: vale para quem ainda vai responder, e quem já respondeu a vê no convite.

export const MICE_QUESTION_TYPES = [
  'text',
  'single_choice',
  'multi_choice',
  'yes_no',
  'number',
  'date',
] as const;
export const MiceQuestionTypeSchema = z.enum(MICE_QUESTION_TYPES);
export type MiceQuestionType = z.infer<typeof MiceQuestionTypeSchema>;

export const MICE_MAX_QUESTIONS = 20;
export const MICE_MAX_QUESTION_OPTIONS = 40;
export const MICE_ANSWER_TEXT_MAX = 1000;
export const MICE_ANSWER_OTHER_MAX = 200;

const QuestionIdSchema = z.string().min(1).max(40);
const OptionIdSchema = z.string().min(1).max(40);

export const MiceQuestionOptionSchema = z.object({
  id: OptionIdSchema,
  label: z.string().trim().min(1).max(80),
  /** Opção tirada pelo organizador: some do convite e fica no resumo das respostas. */
  archived: z.boolean().default(false),
});
export type MiceQuestionOption = z.infer<typeof MiceQuestionOptionSchema>;

const isChoice = (type: MiceQuestionType): boolean =>
  type === 'single_choice' || type === 'multi_choice';

/** O que o organizador define em cada pergunta (sem id nem datas: o mice os dá). */
const QuestionFieldsSchema = z.object({
  type: MiceQuestionTypeSchema,
  label: z.string().trim().min(1).max(200),
  help: z.string().trim().max(300).nullable().default(null),
  required: z.boolean().default(false),
  options: z.array(MiceQuestionOptionSchema).max(MICE_MAX_QUESTION_OPTIONS).default([]),
  /** Escolha com "Outro": abre um campo de texto ao lado das opções. */
  allowOther: z.boolean().default(false),
  /** Múltipla escolha: no máximo quantas; nulo = sem limite. */
  maxSelections: z.number().int().min(1).max(MICE_MAX_QUESTION_OPTIONS).nullable().default(null),
  /** Texto: resposta longa (caixa de várias linhas). */
  multiline: z.boolean().default(false),
  /** Número: faixa aceita; nulo = sem limite. */
  min: z.number().nullable().default(null),
  max: z.number().nullable().default(null),
});

type QuestionFields = z.infer<typeof QuestionFieldsSchema>;

function refineQuestion(q: QuestionFields, ctx: z.RefinementCtx): void {
  const active = q.options.filter((o) => !o.archived);
  if (isChoice(q.type) && active.length < 2) {
    ctx.addIssue({ code: 'custom', path: ['options'], message: 'choice_needs_two_options' });
  }
  if (!isChoice(q.type) && q.options.length > 0) {
    ctx.addIssue({ code: 'custom', path: ['options'], message: 'options_only_for_choice' });
  }
  if (new Set(q.options.map((o) => o.id)).size !== q.options.length) {
    ctx.addIssue({ code: 'custom', path: ['options'], message: 'duplicate_option_id' });
  }
  if (q.min !== null && q.max !== null && q.max < q.min) {
    ctx.addIssue({ code: 'custom', path: ['max'], message: 'max_below_min' });
  }
}

export const MiceQuestionSchema = QuestionFieldsSchema.extend({
  id: QuestionIdSchema,
  createdAt: z.iso.datetime(),
  /** Pergunta tirada pelo organizador: some do convite e fica no resumo das respostas. */
  archivedAt: z.iso.datetime().nullable().default(null),
}).superRefine(refineQuestion);
export type MiceQuestion = z.infer<typeof MiceQuestionSchema>;

/** Uma pergunta nova (o organizador ou o agente): sem id nem datas; opções só com o rótulo. */
export const MiceQuestionDraftSchema = QuestionFieldsSchema.extend({
  options: z
    .array(z.object({ label: z.string().trim().min(1).max(80) }))
    .max(MICE_MAX_QUESTION_OPTIONS)
    .default([]),
});
export type MiceQuestionDraft = z.infer<typeof MiceQuestionDraftSchema>;

export const MiceAnswerValueSchema = z.union([
  z.string().max(MICE_ANSWER_TEXT_MAX),
  z.array(OptionIdSchema).max(MICE_MAX_QUESTION_OPTIONS),
  z.number(),
  z.boolean(),
]);
export type MiceAnswerValue = z.infer<typeof MiceAnswerValueSchema>;

/**
 * A resposta de uma pergunta. `value` por tipo: texto e data (YYYY-MM-DD) → string; escolha única
 * → `[optionId]` (vazio com só "Outro"); múltipla → ids; sim/não → boolean; número → number.
 * `other`: o texto do "Outro".
 */
export const MiceAnswerSchema = z.object({
  questionId: QuestionIdSchema,
  value: MiceAnswerValueSchema,
  other: z.string().trim().max(MICE_ANSWER_OTHER_MAX).nullable().default(null),
});
export type MiceAnswer = z.infer<typeof MiceAnswerSchema>;

export const MiceAnswersSchema = z.array(MiceAnswerSchema).max(MICE_MAX_QUESTIONS * 2);

export type MiceAnswerIssue =
  | 'required'
  | 'wrong_type'
  | 'unknown_option'
  | 'too_many'
  | 'out_of_range'
  | 'invalid_date'
  | 'unknown_question';

/** Sem resposta: nada escolhido, texto vazio, nenhum "Outro". */
export function answerIsEmpty(answer: MiceAnswer | undefined): boolean {
  if (!answer) return true;
  const { value, other } = answer;
  const hasOther = other !== null && other.length > 0;
  if (typeof value === 'string') return value.trim().length === 0;
  if (Array.isArray(value)) return value.length === 0 && !hasOther;
  return false;
}

function numberIssue(question: MiceQuestion, value: MiceAnswerValue): MiceAnswerIssue | null {
  if (typeof value !== 'number' || !Number.isFinite(value)) return 'wrong_type';
  const below = question.min !== null && value < question.min;
  const above = question.max !== null && value > question.max;
  return below || above ? 'out_of_range' : null;
}

function choiceIssue(question: MiceQuestion, answer: MiceAnswer): MiceAnswerIssue | null {
  const { value } = answer;
  if (!Array.isArray(value)) return 'wrong_type';
  const active = new Set(question.options.filter((o) => !o.archived).map((o) => o.id));
  const hasOther = answer.other !== null && answer.other.length > 0;
  if (value.some((id) => !active.has(id)) || (hasOther && !question.allowOther)) {
    return 'unknown_option';
  }
  const picked = value.length + (hasOther ? 1 : 0);
  const limit = question.type === 'single_choice' ? 1 : question.maxSelections;
  return limit !== null && picked > limit ? 'too_many' : null;
}

function issueOf(question: MiceQuestion, answer: MiceAnswer): MiceAnswerIssue | null {
  const { value } = answer;
  switch (question.type) {
    case 'text':
      return typeof value === 'string' ? null : 'wrong_type';
    case 'date':
      if (typeof value !== 'string') return 'wrong_type';
      return z.iso.date().safeParse(value).success ? null : 'invalid_date';
    case 'yes_no':
      return typeof value === 'boolean' ? null : 'wrong_type';
    case 'number':
      return numberIssue(question, value);
    case 'single_choice':
    case 'multi_choice':
      return choiceIssue(question, answer);
    default: {
      const exhaustive: never = question.type;
      return exhaustive;
    }
  }
}

/**
 * Confere as respostas contra as perguntas ATIVAS. `requireAll`: as obrigatórias sem resposta
 * também viram problema (o envio do convite); sem ele, só o que veio errado (o rascunho).
 * Resposta a pergunta arquivada ou desconhecida é `unknown_question`.
 */
export function validateMiceAnswers(
  questions: readonly MiceQuestion[],
  answers: readonly MiceAnswer[],
  options: { requireAll: boolean },
): Array<{ questionId: string; issue: MiceAnswerIssue }> {
  const active = new Map(questions.filter((q) => q.archivedAt === null).map((q) => [q.id, q]));
  const byId = new Map(answers.map((a) => [a.questionId, a]));
  const issues: Array<{ questionId: string; issue: MiceAnswerIssue }> = [];
  for (const answer of answers) {
    const question = active.get(answer.questionId);
    if (!question) {
      issues.push({ questionId: answer.questionId, issue: 'unknown_question' });
      continue;
    }
    if (answerIsEmpty(answer)) continue;
    const issue = issueOf(question, answer);
    if (issue) issues.push({ questionId: question.id, issue });
  }
  if (options.requireAll) {
    for (const question of active.values()) {
      if (question.required && answerIsEmpty(byId.get(question.id))) {
        issues.push({ questionId: question.id, issue: 'required' });
      }
    }
  }
  return issues;
}

/** As perguntas ativas sem resposta (as que o convite mostra como novas a quem já respondeu). */
export function unansweredMiceQuestions(
  questions: readonly MiceQuestion[],
  answers: readonly MiceAnswer[],
): MiceQuestion[] {
  const byId = new Map(answers.map((a) => [a.questionId, a]));
  return questions.filter((q) => q.archivedAt === null && answerIsEmpty(byId.get(q.id)));
}

/** A pergunta como o convidado a vê: só as opções ativas. */
export function activeMiceQuestionView(question: MiceQuestion): MiceQuestion {
  return { ...question, options: question.options.filter((o) => !o.archived) };
}

/** Perguntas prontas: o organizador diz "camiseta" e as opções vêm daqui, não do modelo. */
export const MICE_QUESTION_PRESETS = {
  shirt: {
    type: 'single_choice',
    label: 'Qual o tamanho da sua camiseta?',
    options: ['PP', 'P', 'M', 'G', 'GG', 'XG'],
    allowOther: false,
  },
  flip_flop: {
    type: 'single_choice',
    label: 'Qual o número do seu chinelo?',
    options: ['33/34', '35/36', '37/38', '39/40', '41/42', '43/44', '45/46'],
    allowOther: false,
  },
  diet: {
    type: 'multi_choice',
    label: 'Você tem alguma restrição alimentar?',
    options: ['Nenhuma', 'Vegetariana', 'Vegana', 'Sem glúten', 'Sem lactose'],
    allowOther: true,
  },
} as const satisfies Record<
  string,
  { type: MiceQuestionType; label: string; options: readonly string[]; allowOther: boolean }
>;
type PresetKey = keyof typeof MICE_QUESTION_PRESETS;
const PRESET_KEYS = Object.keys(MICE_QUESTION_PRESETS) as [PresetKey, ...PresetKey[]];
export const MiceQuestionPresetSchema = z.enum(PRESET_KEYS);
export type MiceQuestionPreset = z.infer<typeof MiceQuestionPresetSchema>;

// --- Rotas do organizador (/internal/events/:id/questions) ---------------------------------

/** Perguntas novas, em ordem; `preset` preenche tipo, opções e rótulo que faltarem. */
export const MiceQuestionsAddRequestSchema = z.object({
  organizerId: z.string().min(1),
  questions: z
    .array(
      MiceQuestionDraftSchema.partial({ type: true, label: true }).extend({
        preset: MiceQuestionPresetSchema.optional(),
      }),
    )
    .min(1)
    .max(MICE_MAX_QUESTIONS),
});
export type MiceQuestionsAddRequest = z.infer<typeof MiceQuestionsAddRequestSchema>;

/** Edição de uma pergunta: só o que veio muda. `options` é a lista inteira das ativas: id = a
 *  opção que já existia (rótulo novo), sem id = opção nova; a que sumiu fica arquivada. Mudar
 *  `type` com resposta dada é 409 `question_answered`. */
export const MiceQuestionUpdateRequestSchema = z.object({
  organizerId: z.string().min(1),
  type: MiceQuestionTypeSchema.optional(),
  label: z.string().trim().min(1).max(200).optional(),
  help: z.string().trim().max(300).nullable().optional(),
  required: z.boolean().optional(),
  options: z
    .array(z.object({ id: OptionIdSchema.optional(), label: z.string().trim().min(1).max(80) }))
    .max(MICE_MAX_QUESTION_OPTIONS)
    .optional(),
  allowOther: z.boolean().optional(),
  maxSelections: z.number().int().min(1).max(MICE_MAX_QUESTION_OPTIONS).nullable().optional(),
  multiline: z.boolean().optional(),
  min: z.number().nullable().optional(),
  max: z.number().nullable().optional(),
});
export type MiceQuestionUpdateRequest = z.infer<typeof MiceQuestionUpdateRequestSchema>;

/** Resumo das respostas para o organizador: por pergunta (arquivadas também), quem respondeu o
 *  quê. `respondents` = convidados que aceitaram. Contagens por opção nas de escolha e sim/não. */
export const MiceQuestionSummarySchema = z.object({
  question: MiceQuestionSchema,
  answered: z.number().int().min(0),
  counts: z.array(z.object({ key: z.string(), label: z.string(), count: z.number().int() })),
  answers: z.array(
    z.object({
      participantId: z.string(),
      name: z.string(),
      email: z.string(),
      answer: MiceAnswerSchema,
    }),
  ),
});
export type MiceQuestionSummary = z.infer<typeof MiceQuestionSummarySchema>;

export const MiceQuestionsSummarySchema = z.object({
  respondents: z.number().int().min(0),
  items: z.array(MiceQuestionSummarySchema),
});
export type MiceQuestionsSummary = z.infer<typeof MiceQuestionsSummarySchema>;

// --- Rota do convidado (/internal/invites/:eventId/answers) ---------------------------------

/** Grava as respostas (rascunho ou definitivas); erros em `details.code`: `answers_invalid`
 *  (`issues`) e, no envio do convite, 409 `answers_missing` (`questionIds`). */
export const MiceInviteAnswersSaveRequestSchema = z.object({
  travelerId: NumericIdSchema,
  answers: MiceAnswersSchema,
});
export type MiceInviteAnswersSaveRequest = z.infer<typeof MiceInviteAnswersSaveRequestSchema>;
